// Sauvegarde JSON : export du profil et des contacts, import tolérant (format v3 et export de la v1).
// Règle absolue : une entrée marquée « exemple » n’est jamais importée.
import { normalize } from './time';
import { validateContact, validateProfile, type Contact, type Profile } from './contact';

export const EXPORT_VERSION = 3;

export interface ExportFile {
  version: typeof EXPORT_VERSION;
  app: 'ACTE.5 WORLD';
  exportedAt: string;
  profile: Profile;
  contacts: Contact[];
}

export function serializeExport(profile: Profile, contacts: Contact[], now = Date.now()): string {
  const file: ExportFile = {
    version: EXPORT_VERSION,
    app: 'ACTE.5 WORLD',
    exportedAt: new Date(now).toISOString(),
    profile,
    contacts: contacts.filter((c) => !c.deletedAt),
  };
  return JSON.stringify(file, null, 2);
}

export interface ImportResult {
  contacts: Contact[];
  profile: Profile | null;
  /** Entrées illisibles ignorées. */
  skipped: number;
  /** Exemples de la v1 ignorés. */
  demoSkipped: number;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Signature d’un contact pour détecter les doublons exacts à l’import. */
export function contactSignature(c: Contact): string {
  return [normalize(c.org), normalize(c.name), c.tz, c.start, c.end, [...c.days].sort().join('-')].join('|');
}

export function parseImport(text: string): ImportResult {
  if (text.length > 2 * 1024 * 1024) throw new Error('Les données sont trop volumineuses (2 Mo maximum).');
  let parsed: unknown;
  try { parsed = JSON.parse(text); } catch { throw new Error('Le contenu n’est pas un JSON valide. Copiez l’export complet.'); }
  let list: unknown;
  let profileRaw: Record<string, unknown> | null = null;
  if (Array.isArray(parsed)) list = parsed;
  else if (isRecord(parsed)) {
    list = parsed.contacts ?? parsed.clients;
    if (isRecord(parsed.profile)) profileRaw = parsed.profile;
    else if (isRecord(parsed.me)) {
      // Format de la v1 : `me` porte les horaires ; thème et tri sont à la racine ; label/email deviennent displayName/replyEmail.
      const me = parsed.me;
      profileRaw = { ...me, displayName: me.label, replyEmail: me.email, theme: parsed.theme, sort: parsed.sort };
    }
  }
  if (!Array.isArray(list)) throw new Error('Format de sauvegarde inattendu : aucune liste de contacts.');
  if (list.length > 1000) throw new Error('Le fichier doit contenir 1 000 contacts au maximum.');
  const contacts: Contact[] = [];
  let skipped = 0, demoSkipped = 0;
  for (const raw of list) {
    if (isRecord(raw) && (raw.demo === true || (typeof raw.id === 'string' && raw.id.startsWith('demo-')))) { demoSkipped++; continue; }
    try {
      const c = validateContact(raw, contacts.length);
      if (c.deletedAt) { skipped++; continue; }
      contacts.push(c);
    } catch { skipped++; }
  }
  let profile: Profile | null = null;
  if (profileRaw) { try { profile = validateProfile(profileRaw); } catch { profile = null; } }
  return { contacts, profile, skipped, demoSkipped };
}
